import { Box } from '@mui/material'
import { useDataSentryTrackersByCategory } from 'api/dataSentry/queries/useDataSentryPieChartTrackers'
import { ChartCard } from '../components/ChartCard'
import { useMemo } from 'react'
import { transformTrackerCategoriesData } from '../utils/transformTrackersCategoriesData'
import { formatToK } from '../utils/transformPieChartData'
import { useDataSentryContext } from '../context/DataSentryContext'
import { subDays } from 'date-fns'
import format from 'date-fns/format'
import { GenericPieChart } from '../systems/SystemsTab'
import { MostSeenTrackers } from './mostSeen/MostSeenTrackers'
import { CommonTrackerLineages } from 'pages/dataSentry/trackers/commonTrackerLineages/CommonTrackerLineages'
import { ConsentControl } from 'pages/dataSentry/trackers/consentControl/ConsentControl'
import { DataSentryTrackersTable } from './DataSentryTrackersTable'
import { useDataSentryPieChartTrackersPurpose } from 'api/dataSentry/queries/useDataSentryPieChartTrackersPurpose'
import { transformTrackerPurposePieChart } from '../utils/transformTrackerPurposePieChart'
import { Card, Icon, theme, IconDictionary } from '@ketch-com/deck'
import pluralize from 'pluralize'
import { AuthorizationStatus as TrackersAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { TrackerDetails } from 'pages/dataSentry/trackers/trackerDetails/TrackerDetails'

export const TrackersTab = () => {
  const { selectedTimeRange, selectedPropertyCode, trackersSummary, hasPropertyAndScanTimeRange } =
    useDataSentryContext()

  const { data: categoryTrackerPieData, isLoading: isTrackerCategoryDataLoading } = useDataSentryTrackersByCategory({
    params: {
      'start-date': selectedTimeRange?.startDate || format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      'end-date': selectedTimeRange?.endDate || format(new Date(), 'yyyy-MM-dd'),
      ...(selectedPropertyCode && {
        'property-codes': selectedPropertyCode,
      }),
    },
    enabled: hasPropertyAndScanTimeRange,
  })
  const { data: purposeTrackerPieData, isLoading: isTrackerPurposeDataLoading } = useDataSentryPieChartTrackersPurpose({
    params: {
      'start-date': selectedTimeRange?.startDate || format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      'end-date': selectedTimeRange?.endDate || format(new Date(), 'yyyy-MM-dd'),
      ...(selectedPropertyCode && {
        'property-codes': selectedPropertyCode,
      }),
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const transformedTrackerCategoriesData = useMemo(
    () => transformTrackerCategoriesData(categoryTrackerPieData),
    [categoryTrackerPieData],
  )

  const transformedTrackerPurposeData = useMemo(() => {
    if (!purposeTrackerPieData) return []
    return transformTrackerPurposePieChart(purposeTrackerPieData)
  }, [purposeTrackerPieData])

  const unauthorizedTrackers = trackersSummary?.authorizationTypes?.find(
    tracker => tracker.status === TrackersAuthorizationStatus.UNAUTHORIZED,
  )

  const unauthorizedTrackersCount = unauthorizedTrackers?.count || 0

  return (
    <Box width="100%" mx="auto" display="flex" flexDirection="column" gap={6} pb={16} mt={3}>
      <Box display="flex" gap={3}>
        <ChartCard title="Tracker Categories" isLoading={isTrackerCategoryDataLoading}>
          <GenericPieChart data={transformedTrackerCategoriesData} isLoading={isTrackerCategoryDataLoading} />
        </ChartCard>
        <Box display="flex" flexDirection="column" flexBasis={260} gap={2}>
          <Card
            cardVariant="stretchWidth"
            cardTitle={formatToK(trackersSummary?.total || 0)}
            subTitle={pluralize('Tracker', trackersSummary?.total)}
            primaryIcon={
              <Icon name={IconDictionary.Trackers} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
            }
            isNumerical
            backgroundColor={trackersSummary?.total ? theme.palette.White.o100 : 'transparent'}
            textColor={trackersSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
            sx={{
              border: trackersSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
              padding: 2,
            }}
          />

          <Card
            cardVariant="stretchWidth"
            cardTitle={formatToK(unauthorizedTrackersCount || 0)}
            subTitle={pluralize('Unauthorized Tracker', unauthorizedTrackersCount)}
            primaryIcon={
              <Icon
                name={IconDictionary.OCookieUnknown}
                width="32px"
                height="32px"
                iconColor={theme.palette.Black.o48}
              />
            }
            isNumerical
            backgroundColor={unauthorizedTrackersCount ? theme.palette.White.o100 : 'transparent'}
            textColor={unauthorizedTrackersCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
            sx={{
              border: unauthorizedTrackersCount ? 'none' : `1px solid ${theme.palette.Black.o8}`,
              padding: 2,
            }}
          />
        </Box>
        <ChartCard title="Tracker Purposes" isLoading={isTrackerPurposeDataLoading}>
          <GenericPieChart data={transformedTrackerPurposeData} isLoading={isTrackerPurposeDataLoading} />
        </ChartCard>
      </Box>

      <Box display="flex" gap={3} minHeight={480}>
        <ConsentControl />
        <MostSeenTrackers />
      </Box>

      <CommonTrackerLineages />
      <DataSentryTrackersTable />
      <TrackerDetails />
    </Box>
  )
}
